<template>
  <div id="app" class="bg-[#fff] max-w-[800px] mx-auto relative min-h-[110vh] font-montserrat">
    <router-view v-if="isReady"/>
    <div v-else class="fixed inset-0 bg-primary items-center flex justify-center z-50 text-center px-4">
      <div v-if="!errorLoader" class="flex flex-col gap-3">
        <div class="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
        <div class="text-lg font-semibold text-white">Mohon tunggu...</div>
      </div>
      <div v-else>
        <div class="text-white text-lg font-bold">Gagal memuat data</div>
        <button
          @click="retryFetch"
          class="mt-4 bg-white text-primary px-4 py-2 rounded-lg font-semibold hover:bg-red-100 transition"
        >
          Coba Lagi
        </button>
      </div>
    </div>

    <NavBar v-if="isNavBarVisible" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { App } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
// import { Capacitor } from '@capacitor/core';
import NavBar from './components/Nav.vue';
// import Pusher from 'pusher-js';
import { useAcademicYearStore } from './stores/useAcademicYearStore';
import { useChildrenStore } from './stores/useChildrenStore';
import { useProfileStore } from './stores/useProfileStore';
import { RoleEnum } from './enums/RoleEnum';
import { useNotificationStore } from './stores/useNotificationStore';
import { useConfigStore } from './stores/useConfigStore';

const route = useRoute();
const router = useRouter();

const academicStore = useAcademicYearStore();
const childrenStore = useChildrenStore();
const profileStore = useProfileStore();
const notificationStore = useNotificationStore();
const configStore = useConfigStore();

const isReady = ref(false);
const errorLoader = ref(false);
const isNavBarVisible = computed(() => ['Home', 'Publication', 'Academic', 'Notification'].includes(route.name));

let isParent;

watch(() => profileStore.profile, (newVal) => {
  if (!newVal) return;
  isParent = profileStore.profile.roles.some(role => role.name === RoleEnum.PARENTS);
});

const checkExcludedRoute = async () => {
  try {
    const excludedRoutes = ['Login', 'Register', 'SplashScreen'];
    if (excludedRoutes.includes(route.name)) {
      isReady.value = true;
      return;
    }

    await academicStore.fetchAcademicYear();

    await profileStore.fetchProfile();
    
    await configStore.fetchConfig();
    
    await fetchData();
    isReady.value = true;
  } catch (err) {
    console.error('Gagal fetch awal:', err);
    errorLoader.value = true;
  }
};

const fetchData = async () => {
  try {
    const profile = profileStore.profile;

    if (isParent) {
      await childrenStore.fetchChildrenList(profile.id);
      // initializePusher(childrenStore.children);
    } else {
      // initializePusher();
    }

    notificationStore.fetchNotificationCount();
  } catch (err) {
    console.error('Error in fetchData:', err);
  }
};

// const initializePusher = (children = []) => {
//   const profile = profileStore.profile;
//   const pusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
//     cluster: process.env.VUE_APP_PUSHER_CLUSTER,
//   });
//   const channel = pusher.subscribe(`${process.env.VUE_APP_SCHOOL_NAME_SLUG}-mobile-notification`);

//   const bindToChannel = (userId) => {
//     channel.bind(`attendance-${userId}`, showPushNotification);
//     channel.bind(`notify-mobile-teacher-note-${userId}`, showPushNotification);
//     channel.bind(`update-data-${userId}`, updateData);
//     channel.bind(`update-wallet-${userId}`, updateWallet)
//   };

//   if (children.length > 0) {
//     children.forEach(child => bindToChannel(child.user_id));
//   } else {
//     bindToChannel(profile.id);
//   }

//   profile.roles.forEach(role => {
//     channel.bind(`notify-mobile-${role.id}`, showPushNotification);
//     channel.bind(`update-data-${role.id}`, updateData);
//   });
// };

// const showPushNotification = async (message) => {
//   await store.dispatch('user/fetchNotifications');
//   await store.dispatch('user/countNotification');

//   const notificationData = {
//     title: message.data.title,
//     body: message.data.description,
//   };

//   if (Capacitor.isNativePlatform()) {
//     await LocalNotifications.schedule({
//       notifications: [
//         {
//           id: Date.now(),
//           ...notificationData,
//           schedule: { at: new Date(Date.now() + 1000) },
//           extra: { redirectTo: 'Notification' },
//         },
//       ],
//     });
//   } else {
//     if (Notification.permission === 'granted') {
//       new Notification(notificationData.title, { body: notificationData.body });
//     } else if (Notification.permission !== 'denied') {
//       Notification.requestPermission().then((permission) => {
//         if (permission === 'granted') {
//           new Notification(notificationData.title, { body: notificationData.body });
//         }
//       });
//     }
//   }

//   saveNotification(notificationData);
// };

// const saveNotification = (message) => {
//   const notification = {
//     title: message.title,
//     message: message.body,
//     time: new Date().toISOString(),
//     read: false,
//     id: Math.floor(Math.random() * 1000000),
//   };

//   const existing = JSON.parse(localStorage.getItem('notifications') || '[]');
//   existing.push(notification);
//   localStorage.setItem('notifications', JSON.stringify(existing));
// };

// const updateData = async (data) => {
//   const profile = profileStore.profile;
//   const childId = profile?.student?.id || childrenStore.selectedChild?.id;

//   if (data.type === 'homework' && childId) {
//     await store.dispatch('children/fetchHomeworkList', childId);
//   }
// };

// const updateWallet = async () => {
//   if(isParent) {
//     childrenStore.fetchChildrenList(profileStore.profile.id, true)
//   } else {
//     profileStore.fetchProfile(true);
//   }
// };

const handleNotificationClick = (notification) => {
  const redirect = notification.notification.extra?.redirectTo || notification.notification.data?.redirectTo;
  if (redirect === 'Notification') {
    router.push({ name: 'Notification' });
  }
};

const handleBackButton = () => {
  const currentPath = router.currentRoute.value.path;
  if (currentPath === '/home' && confirm('Apakah kamu ingin keluar dari aplikasi?')) {
    App.exitApp();
  } else {
    window.history.back();
  }
};

const retryFetch = async () => {
  errorLoader.value = false;
  isReady.value = false;
  await checkExcludedRoute();
  if (!isReady.value) errorLoader.value = true;
};

watch(() => route.path, (path) => {
  document.body.classList.toggle('scale-x-[-1]', path === '/scan-qr-code');
});

watch(() => route.fullPath, () => {
  checkExcludedRoute();
});

onMounted(async () => {
  App.addListener('backButton', handleBackButton);
  LocalNotifications.addListener('localNotificationActionPerformed', handleNotificationClick);

  await router.isReady(); // pastiin route udah siap
  checkExcludedRoute();
});

onUnmounted(() => {
  window.removeEventListener('force-refetch', checkExcludedRoute);
});
</script>